import { useState } from "react";
import { Calendar, X } from "lucide-react";
import { errorToast } from "@telluscompany/design-system";

import { useLocalization } from "@i18n";
import { useUserContext } from "@shared/contexts";

import {
  useCreateInspectionMutation,
  type InspectionListItem,
  type InspectionParticipantUser,
} from "../../api/inspection";
import { todayInspectionIsoDate } from "../../utils/inspectionDate";
import { isDuplicateParticipant } from "../../utils/inspectionParticipantIdentity";
import InspectionExtraParticipantFields, {
  type ExtraParticipantSource,
} from "./InspectionExtraParticipantFields";

export interface InspectionCreateFormProps {
  structureId: string;
  onCreated: (created: InspectionListItem) => void;
  onCancel: () => void;
}

type ExtraDraft = {
  name: string;
  participantUserId?: string;
};

/**
 * Cabeçalho de Nova inspeção (form §7.2) — módulo, não ficha: data, responsável
 * (sempre a sessão, somente leitura) e extras opcionais. POST Y=4; o back
 * carimba `templateVersion`. Extra: só nome ou usuário do tenant (Y=15).
 */
const InspectionCreateForm = ({
  structureId,
  onCreated,
  onCancel,
}: InspectionCreateFormProps) => {
  const { translate } = useLocalization();
  const { user } = useUserContext();

  const [date, setDate] = useState(todayInspectionIsoDate);
  const [extraSource, setExtraSource] = useState<ExtraParticipantSource>("gs3User");
  const [extraName, setExtraName] = useState("");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedUser, setSelectedUser] =
    useState<InspectionParticipantUser | null>(null);
  const [extras, setExtras] = useState<ExtraDraft[]>([]);

  const { mutate: createInspection, isPending } =
    useCreateInspectionMutation(structureId);

  const resetExtraDraft = () => {
    setExtraName("");
    setSelectedUserId("");
    setSelectedUser(null);
  };

  const handleSourceChange = (source: ExtraParticipantSource) => {
    setExtraSource(source);
    resetExtraDraft();
  };

  const existingIdentities = [
    { name: user?.name ?? "", participantUserId: user?.id ?? null },
    ...extras,
  ];

  const excludedUserIds = existingIdentities
    .map((item) => item.participantUserId)
    .filter((userId): userId is string => Boolean(userId));

  const addExtra = () => {
    if (extraSource === "gs3User") {
      if (!selectedUser) return;

      const next = {
        name: selectedUser.name,
        participantUserId: selectedUser.id,
      };
      if (isDuplicateParticipant(next, existingIdentities)) {
        errorToast({
          description: translate("inspection.form.extraDuplicate"),
        });
        return;
      }

      setExtras((current) => [...current, next]);
      resetExtraDraft();
      return;
    }

    const name = extraName.trim();
    if (!name) return;

    const next = { name };
    if (isDuplicateParticipant(next, existingIdentities)) {
      errorToast({
        description: translate("inspection.form.extraDuplicate"),
      });
      return;
    }

    setExtras((current) => [...current, next]);
    resetExtraDraft();
  };

  const handleSubmit = () =>
    createInspection(
      {
        inspectionDate: date,
        ...(extras.length > 0
          ? {
              participants: extras.map((extra) =>
                extra.participantUserId
                  ? { name: extra.name, participantUserId: extra.participantUserId }
                  : { name: extra.name },
              ),
            }
          : {}),
      },
      { onSuccess: (created) => onCreated(created) },
    );

  const responsibleValue = user?.name ?? "";

  return (
    <div className="insp-nested-shell">
      <div className="insp-nested-body insp-create-stack">
        <div className="insp-field">
          <label htmlFor="inspection-create-responsible">
            {translate("inspection.form.generalData.responsible")}
          </label>
          <input
            id="inspection-create-responsible"
            readOnly
            value={responsibleValue}
            className="insp-inp readonly"
          />
        </div>

        <div className="insp-field">
          <label htmlFor="inspection-create-date">
            {translate("inspection.form.generalData.date")}
            {" "}
            <span className="insp-req" aria-hidden>
              *
            </span>
          </label>
          <div className="insp-inp-icon">
            <input
              id="inspection-create-date"
              type="date"
              aria-label={translate("inspection.form.generalData.date")}
              value={date}
              onChange={(event) => setDate(event.target.value)}
              className="insp-inp"
            />
            <Calendar aria-hidden size={16} className="insp-inp-icon-mark" />
          </div>
        </div>

        <div className="insp-create-extras">
          <div className="insp-create-extras-head">
            <span className="insp-create-extras-title">
              {translate("inspection.form.generalData.extraParticipants")}
            </span>
          </div>
          <InspectionExtraParticipantFields
            structureId={structureId}
            groupName="create-extra-source"
            source={extraSource}
            onSourceChange={handleSourceChange}
            name={extraName}
            onNameChange={setExtraName}
            nameId="inspection-create-extra-name"
            selectedUserId={selectedUserId}
            onUserChange={(userId, user) => {
              setSelectedUserId(userId);
              setSelectedUser(user);
            }}
            onNameSubmit={addExtra}
            requiredMark={false}
            excludedUserIds={excludedUserIds}
            trailingAction={
              <button
                type="button"
                className="insp-btn insp-btn-outline"
                onClick={addExtra}
              >
                {translate("inspection.form.generalData.addExtraParticipant")}
              </button>
            }
          />

          {extras.length > 0 ? (
            <div className="insp-chips">
              {extras.map((extra, index) => (
                <span
                  key={`${extra.name}-${extra.participantUserId ?? "name"}-${index}`}
                  data-testid="inspection-create-extra"
                  className="insp-chip"
                >
                  {extra.name}
                  <button
                    type="button"
                    aria-label={translate("inspection.participants.remove")}
                    onClick={() =>
                      setExtras((current) =>
                        current.filter((_, position) => position !== index),
                      )
                    }
                  >
                    <X aria-hidden size={14} />
                  </button>
                </span>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      <div className="insp-modal-foot">
        <button
          type="button"
          className="insp-btn insp-btn-outline"
          onClick={onCancel}
        >
          {translate("inspection.actions.cancel")}
        </button>
        <button
          type="button"
          disabled={isPending}
          className="insp-btn insp-btn-primary"
          onClick={handleSubmit}
        >
          {translate("inspection.actions.save")}
        </button>
      </div>
    </div>
  );
};

export default InspectionCreateForm;
